/**
 * THE CTS ODDS TOOLTIP — text only, five lines at most.
 *
 * <pre>
 *   [CTS Signal — Zone 1 Long]
 *   Historical Reversal Odds: ~78%
 *   Based on 34 prior similar setups (left-only)
 *   Base 61% (95% CI 44–76%) · zone +12 · distance +5 · regime −1
 *   Context: 2.31h from equator, Accumulation, RSI divergence agrees
 * </pre>
 *
 * <h2>The BREAKDOWN line is not decoration</h2>
 *
 * The brief's layout has four lines and this has five. The extra one is where the number came
 * from — the base rate it started at, the interval around that base, and what each rule moved
 * it by. Without it `~78%` is a figure a reader can only accept or reject whole; with it they
 * can see that it is a 61% base plus a zone adjustment measured on its own cohort, and decide
 * whether they believe that chain. Every other surface in this application reports a rate with
 * its interval for the same reason, and a tooltip quoting odds with neither would be the one
 * place that does not.
 *
 * <h2>It formats and nothing else</h2>
 *
 * No arithmetic beyond turning a fraction into a percentage. A tooltip that computed would be a
 * second answer to a question the engine has already answered, a pixel from the mark it
 * describes.
 */

const pctOf = (v, decimals = 0) =>
  (typeof v === 'number' && Number.isFinite(v) ? `${(v * 100).toFixed(decimals)}%` : 'n/a');

const points = (v) => {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  const p = v * 100;
  if (Math.abs(p) < 0.5) return '0';
  return `${p > 0 ? '+' : '−'}${Math.abs(p).toFixed(0)}`;
};

/** Zone 1 Long / Zone 3 Short / Long when no channel is in force. */
export function signalTitle(record) {
  if (!record) return 'CTS Signal';
  const side = record.direction === 'down' ? 'Short' : 'Long';
  const zone = record.zone ? `${record.zone.replace('_', ' ')} ` : '';
  const cat = record.category ? ` · ${record.category}` : '';
  return `CTS Signal — ${zone}${side}${cat}`;
}

/**
 * The context clause: the features that are actually present, named in plain words.
 *
 * <p>Only what is TRUE of this bar. A context line listing every feature with "no" beside the
 * absent ones would be four times as long and would bury the two that matter.
 */
export function contextClause(record) {
  const parts = [];
  if (typeof record.absDistance === 'number' && Number.isFinite(record.absDistance)) {
    const d = record.absDistance;
    const word = d >= 2 ? 'Far from equator' : d >= 1 ? 'Extended from equator'
      : d >= 0.5 ? 'Mid-channel' : 'Close to equator';
    parts.push(`${word} (${d.toFixed(2)}h)`);
  }
  if (record.regime && record.regime !== 'UNKNOWN') {
    parts.push(`${record.regime.charAt(0)}${record.regime.slice(1).toLowerCase()} regime`);
  }
  if (record.divergence) {
    parts.push(record.divergenceAgrees
      ? 'RSI divergence agrees'
      : 'RSI divergence CONFLICTS');
  }
  return parts.length > 0 ? parts.join(', ') : 'No qualifying context at this bar';
}

/**
 * The whole tooltip, as the lines the marker renderer draws.
 *
 * @param {object} record one entry from `ctsOddsSeries`
 * @returns {string[]} between two and five lines, never empty
 */
export function oddsTooltipLines(record) {
  if (!record) return [];
  const head = `[${signalTitle(record)}]`;

  if (record.beyondBoundary) {
    // SHORTER than the cold-start card, deliberately: there is no sample to report and no
    // context to describe, because every field that would carry one is off the end of the
    // drawn chart. A card padded out with "0 prior setups" would imply it had looked.
    return [head, record.reason];
  }

  if (record.insufficient) {
    return [
      head,
      record.reason,
      // The count is still worth stating. "Insufficient" with no number leaves a reader unable
      // to tell a brand-new chart from one that is two precedents short.
      `${record.sample} prior resolved setup${record.sample === 1 ? '' : 's'} before this bar`
        + (Number.isFinite(record.minSample) ? ` (need ${record.minSample})` : ''),
      `Context: ${contextClause(record)}`,
    ];
  }

  const ci = record.baseInterval;
  const applied = record.adjustments.filter(a => a.applied && points(a.delta) !== '0');
  // The NET is printed whenever it differs from the deltas beside it, and that is the whole
  // reason it exists: the deltas are what the cohorts measured, and two bounds sit between them
  // and the answer — the correlated-evidence cap, and the headroom scale that stops a +14
  // measured against a 50% base being applied whole against an 86% one. Without this clause a
  // reader adding +14 to 86% and reading 90% would see an arithmetic error.
  const net = record.capped || record.scaled
    ? [`net ${points(record.adjustmentApplied)}`
        + (record.capped ? ' (capped, headroom)' : ' (headroom)')]
    : [];
  const breakdown = [
    `Base ${pctOf(record.baseRate)}`
      + (ci ? ` (95% CI ${pctOf(ci.low)}–${pctOf(ci.high)})` : ''),
    ...applied.map(a => `${a.label} ${points(a.delta)}`),
    ...net,
  ].join(' · ');

  return [
    head,
    `Historical Reversal Odds: ~${pctOf(record.odds)}`,
    `Based on ${record.sample} prior similar setup${record.sample === 1 ? '' : 's'} (left-only)`,
    breakdown,
    `Context: ${contextClause(record)}`,
  ];
}

/**
 * The one-line name shown above the lines — the renderer draws `name` in bold over them.
 *
 * <p>The DATE, because that is what a reader hovering a dense chart most needs to confirm: the
 * bar under the cursor is genuinely ambiguous when three decades are on screen, and the odds
 * are about a specific bar.
 */
export function oddsTooltipTitle(record) {
  if (!record) return '';
  return record.date || '';
}

export default oddsTooltipLines;
